<template>
  <div class="main-content">
    <!-- Breadcrumb / header -->
    <div
      v-if="node.id"
      class="row mb-2"
    >
      <div class="col-md-12">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="GitBranchIcon"
            size="22"
            class="mr-1 text-primary"
          />
          <div>
            <h4 class="mb-0">
              {{ node.name }}
            </h4>
            <small class="text-muted">
              {{ node.type ? node.type.name : "" }}
              <span v-if="node.parent">
                &nbsp;·&nbsp;
                <feather-icon
                  icon="CornerDownRightIcon"
                  size="12"
                />
                {{ node.parent.name }}
              </span>
            </small>
          </div>
          <div class="ml-auto">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="$router.push('/nodes')"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="14"
                class="mr-50"
              />
              Voltar
            </b-button>
          </div>
        </div>
        <hr>
      </div>
    </div>

    <b-tabs
      v-if="node.id"
      content-class="mt-2"
      @input="onTabChange"
    >
      <!-- ─── ABA DETALHES ─────────────────────────────────────────────── -->
      <b-tab
        title="Detalhes"
        active
      >
        <div class="card">
          <div class="card-body">
            <div class="form-row">
              <div class="form-group col-md-6">
                <label><span class="required">*</span> Nome:</label>
                <input
                  v-model="node.name"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-6">
                <label><span class="required">*</span> Tipo:</label>
                <v-select
                  v-model="node.type_id"
                  :options="nodeTypes"
                  :reduce="(t) => t.id"
                  label="name"
                  class="vselect"
                  placeholder="Selecione o tipo"
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Nó Pai
                  <small class="text-muted">(deixe vazio para raiz)</small>:</label>
                <v-select
                  v-model="node.parent_id"
                  :options="allNodes.filter((n) => n.id !== node.id)"
                  :reduce="(n) => n.id"
                  label="name"
                  class="vselect"
                  placeholder="Sem nó pai (raiz)"
                  :clearable="true"
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Tenant ID
                  <small class="text-muted">(opcional)</small>:</label>
                <input
                  v-model="node.tenant_id"
                  type="number"
                  class="form-control"
                >
              </div>
            </div>
            <!-- Campos dinâmicos do tipo selecionado -->
            <div
              v-if="fieldSchema.length"
              class="mt-1"
            >
              <hr>
              <p class="font-weight-bold mb-2">
                Informações do tipo
                <em>{{ node.type ? node.type.name : "" }}</em>
              </p>
              <div class="form-row">
                <div
                  v-for="field in fieldSchema"
                  :key="field.key"
                  :class="['form-group', field.col || 'col-md-6']"
                >
                  <label>
                    <span
                      v-if="field.required"
                      class="required"
                    >*</span>
                    {{ field.label }}:
                  </label>
                  <input
                    v-if="field.type === 'text'"
                    v-model="node.metadata[field.key]"
                    type="text"
                    class="form-control"
                  >
                  <input
                    v-else-if="field.type === 'number'"
                    v-model="node.metadata[field.key]"
                    type="number"
                    class="form-control"
                  >
                  <textarea
                    v-else-if="field.type === 'textarea'"
                    v-model="node.metadata[field.key]"
                    class="form-control"
                    rows="3"
                  />
                  <select
                    v-else-if="field.type === 'select'"
                    v-model="node.metadata[field.key]"
                    class="form-control"
                  >
                    <option value="">
                      Selecione
                    </option>
                    <option
                      v-for="opt in field.options"
                      :key="opt"
                      :value="opt"
                    >
                      {{ opt }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <b-button
              v-if="$hasAbility('node.admin')"
              variant="primary"
              @click="saveNode"
            >Salvar Detalhes</b-button>
          </div>
        </div>
      </b-tab>

      <!-- ─── ABA EMPRESA ──────────────────────────────────────────────── -->
      <b-tab :title="company.id ? 'Empresa ✓' : 'Empresa'">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <span class="card-title mb-0">
              {{
                company.id ? "Empresa vinculada a " : "Cadastrar empresa para "
              }}<strong>{{ node.name }}</strong>
            </span>
            <b-button
              v-if="company.id && $hasAbility('node.admin')"
              size="sm"
              variant="outline-danger"
              @click="unlinkCompany"
            >
              <feather-icon
                icon="XIcon"
                size="14"
                class="mr-50"
              />
              Desvincular
            </b-button>
          </div>
          <div class="card-body">
            <div class="form-row">
              <div class="form-group col-md-5">
                <label><span class="required">*</span> Nome:</label>
                <input
                  v-model="company.name"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-4">
                <label>Nome Fantasia:</label>
                <input
                  v-model="company.fantasy_name"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-3">
                <label><span class="required">*</span> CNPJ:</label>
                <input
                  v-model="company.cnpj"
                  v-mask="'99.999.999/9999-99'"
                  type="text"
                  class="form-control"
                >
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-4">
                <label><span class="required">*</span> Tipo da Empresa:</label>
                <input
                  v-model="company.type"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-3">
                <label>Telefone:</label>
                <input
                  v-model="company.phone"
                  v-mask="'(99) 99999-9999'"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-2">
                <label>CEP:</label>
                <input
                  v-model="company.cep"
                  v-mask="'99999-999'"
                  type="text"
                  class="form-control"
                  @keyup="buscarCep"
                >
              </div>
              <div class="form-group col-md-3">
                <label>Status:</label>
                <select
                  v-model="company.status"
                  class="form-control"
                >
                  <option :value="1">
                    Ativo
                  </option>
                  <option :value="0">
                    Inativo
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-4">
                <label>Logradouro:</label>
                <input
                  v-model="company.address"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-3">
                <label>Bairro:</label>
                <input
                  v-model="company.neighborhood"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-1">
                <label>Número:</label>
                <input
                  id="inputNumeroEmpresa"
                  v-model="company.number"
                  type="text"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-4">
                <label>Complemento:</label>
                <input
                  v-model="company.complement"
                  type="text"
                  class="form-control"
                >
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-3">
                <label>UF:</label>
                <v-select
                  v-model="company.state_id"
                  :options="ufs"
                  :reduce="(u) => u.id"
                  label="name"
                  class="vselect"
                  @input="getCities"
                />
              </div>
              <div class="form-group col-md-5">
                <label>Cidade:</label>
                <v-select
                  v-model="company.city_id"
                  :options="cities"
                  :reduce="(c) => c.id"
                  label="name"
                  class="vselect"
                />
              </div>
              <div class="form-group col-md-4">
                <label>Referência:</label>
                <input
                  v-model="company.reference"
                  type="text"
                  class="form-control"
                >
              </div>
            </div>
            <b-button
              v-if="$hasAbility('node.admin')"
              variant="primary"
              @click="saveCompany"
            >
              <feather-icon
                icon="SaveIcon"
                size="14"
                class="mr-50"
              />
              {{ company.id ? "Salvar Empresa" : "Cadastrar Empresa" }}
            </b-button>
          </div>
        </div>
      </b-tab>

      <!-- ─── ABA SUB-NÓS ───────────────────────────────────────────────── -->
      <b-tab :title="`Sub-nós (${children.length})`">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <span
              class="card-title mb-0"
            >Nós filhos de <strong>{{ node.name }}</strong></span>
            <b-button
              v-if="$hasAbility('node.admin')"
              size="sm"
              variant="primary"
              @click="openAddChild"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-50"
              />
              Adicionar Sub-nó
            </b-button>
          </div>
          <div class="card-body p-0">
            <table
              v-if="children.length"
              class="table table-hover mb-0"
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Usuários</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="child in children"
                  :key="child.id"
                >
                  <td>{{ child.id }}</td>
                  <td>{{ child.name }}</td>
                  <td>{{ child.type ? child.type.name : "-" }}</td>
                  <td>{{ child.node_users_count || 0 }}</td>
                  <td>
                    <b-button
                      v-if="$hasAbility('node.ver')"
                      size="sm"
                      variant="outline-primary"
                      class="mr-1"
                      title="Administrar"
                      @click="goToAdmin(child.id)"
                    >
                      <feather-icon
                        icon="SettingsIcon"
                        size="14"
                      />
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      size="sm"
                      variant="outline-danger"
                      title="Remover do pai"
                      @click="unlinkChild(child)"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="14"
                      />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-else
              class="p-3 text-center text-muted"
            >
              Nenhum sub-nó cadastrado.
            </div>
          </div>
        </div>

        <!-- Modal sub-nó -->
        <b-modal
          id="modal-add-child"
          title="Adicionar Sub-nó"
          ok-title="Salvar"
          cancel-title="Cancelar"
          @ok="saveChild"
        >
          <div class="form-group">
            <label>Vincular nó existente como filho:</label>
            <v-select
              v-model="newChildId"
              :options="availableChildren"
              :reduce="(n) => n.id"
              label="name"
              class="vselect"
              placeholder="Selecione o nó filho"
            />
          </div>
          <hr>
          <p class="text-muted small mb-1">
            ou crie um novo nó filho:
          </p>
          <b-button
            v-if="$hasAbility('node.admin')"
            size="sm"
            variant="outline-primary"
            @click="$router.push('/nodes/add?parent_id=' + node.id)"
          >
            <feather-icon
              icon="PlusIcon"
              size="13"
              class="mr-50"
            />
            Criar novo nó filho
          </b-button>
        </b-modal>
      </b-tab>

      <!-- ─── ABA PROFISSIONAIS ─────────────────────────────────────────── -->
      <b-tab :title="`Profissionais (${nodeProfessionals.length})`">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <span
              class="card-title mb-0"
            >Profissionais vinculados a <strong>{{ node.name }}</strong></span>
            <b-button
              v-if="$hasAbility('node.admin')"
              size="sm"
              variant="primary"
              @click="openAddProfessional"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-50"
              />
              Vincular Profissional
            </b-button>
          </div>
          <div class="card-body p-0">
            <table
              v-if="nodeProfessionals.length"
              class="table table-hover mb-0"
            >
              <thead>
                <tr>
                  <th>Profissional</th>
                  <th>Tipo de Profissional</th>
                  <th>Papel</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="np in nodeProfessionals"
                  :key="np.id"
                >
                  <td>
                    {{
                      np.professional
                        ? np.professional.name
                        : np.professional_id
                    }}
                  </td>
                  <td>
                    {{ np.professional_type ? np.professional_type.name : "-" }}
                  </td>
                  <td>{{ np.role ? np.role.name : "-" }}</td>
                  <td>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      size="sm"
                      variant="outline-danger"
                      title="Desvincular"
                      @click="unlinkProfessional(np)"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="14"
                      />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-else
              class="p-3 text-center text-muted"
            >
              Nenhum profissional vinculado.
            </div>
          </div>
        </div>

        <!-- Modal profissional -->
        <b-modal
          id="modal-add-professional"
          title="Vincular Profissional ao Nó"
          ok-title="Vincular"
          cancel-title="Cancelar"
          @ok="saveProfessional"
        >
          <div class="form-group">
            <label>Profissional:</label>
            <v-select
              v-model="newProfessional.professional_id"
              :options="availableProfessionals"
              :reduce="(p) => p.id"
              label="name"
              class="vselect"
              placeholder="Selecione o profissional"
            />
          </div>
          <div class="form-group">
            <label>Tipo de Profissional:</label>
            <v-select
              v-model="newProfessional.professional_type_id"
              :options="allProfessionalTypes"
              :reduce="(t) => t.id"
              label="name"
              class="vselect"
              placeholder="Selecione o tipo (ex: Médico, Enfermeiro, Condutor)"
            />
          </div>
          <div class="form-group">
            <label>Papel
              <small class="text-muted">— permissões de acesso</small>:</label>
            <v-select
              v-model="newProfessional.role_id"
              :options="allRoles"
              :reduce="(r) => r.id"
              label="name"
              class="vselect"
              placeholder="Selecione o papel (opcional)"
            />
          </div>
        </b-modal>
      </b-tab>

      <!-- Disciplinas -->
      <b-tab :title="`Disciplinas (${selectedDisciplineIds.length})`">
        <div class="card">
          <div
            class="card-header d-flex justify-content-between align-items-center"
          >
            <span
              class="card-title mb-0"
            >Disciplinas atendidas por <strong>{{ node.name }}</strong></span>
            <small class="text-muted">
              {{ selectedDisciplineIds.length }} de
              {{ allDisciplines.length }} ativas
            </small>
          </div>
          <div class="card-body p-0">
            <p
              v-if="!allDisciplines.length"
              class="text-muted p-3 mb-0"
            >
              Nenhuma disciplina cadastrada no sistema.
            </p>
            <table
              v-else
              class="table table-hover mb-0"
            >
              <thead>
                <tr>
                  <th style="width: 56px">
                    Ativo
                  </th>
                  <th>Disciplina</th>
                  <th>Descrição</th>
                  <th style="width: 60px" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="disc in allDisciplines"
                  :key="disc.id"
                >
                  <td class="align-middle">
                    <b-form-checkbox
                      :checked="selectedDisciplineIds.includes(disc.id)"
                      switch
                      @change="toggleDiscipline(disc.id)"
                    />
                  </td>
                  <td class="align-middle">
                    <span
                      :class="
                        selectedDisciplineIds.includes(disc.id)
                          ? 'font-weight-bold text-primary'
                          : 'text-muted'
                      "
                    >{{ disc.name }}</span>
                  </td>
                  <td class="align-middle">
                    <small class="text-muted">{{
                      disc.description || "—"
                    }}</small>
                  </td>
                  <td class="align-middle text-center">
                    <b-button
                      v-if="$hasAbility('node.ver')"
                      size="sm"
                      variant="outline-secondary"
                      title="Configurar disciplina"
                      @click="openDisciplineConfig(disc)"
                    >
                      <feather-icon
                        icon="SettingsIcon"
                        size="14"
                      />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="card-footer d-flex align-items-center justify-content-between"
          >
            <b-button
              v-if="$hasAbility('node.admin')"
              variant="primary"
              size="sm"
              @click="saveDisciplines"
            >
              <feather-icon
                icon="SaveIcon"
                size="14"
                class="mr-50"
              />
              Salvar
            </b-button>
            <b-button
              v-if="selectedDisciplineIds.length"
              variant="outline-secondary"
              size="sm"
              @click="selectedDisciplineIds = []"
            >
              Limpar seleção
            </b-button>
          </div>
        </div>

        <!-- Modal configurar disciplina removido — usa tela dedicada /nodes/admin/:id/discipline/:disciplineId -->
      </b-tab>

      <!-- ─── ABA ABRANGÊNCIA ────────────────────────────────────────────── -->
      <b-tab :title="`Abrangência (${coverages.length})`">
        <div class="row">
          <!-- Formulário de adicionar cobertura -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">Adicionar Abrangência</span>
              </div>
              <div class="card-body">
                <!-- Banner de restrição herdada do nó pai -->
                <div
                  v-if="parentConstraint"
                  class="alert alert-info py-1 px-2 small mb-2 d-flex align-items-start"
                >
                  <feather-icon
                    icon="InfoIcon"
                    size="14"
                    class="mr-1 mt-25 flex-shrink-0"
                  />
                  <span>{{ parentConstraint.message }}</span>
                </div>

                <div class="form-group">
                  <label>Tipo:</label>
                  <v-select
                    v-model="newCoverage.coverage_type"
                    :options="coverageTypeOptions"
                    :reduce="(o) => o.value"
                    label="label"
                    class="vselect"
                    placeholder="Selecione o tipo..."
                    @input="onCoverageTypeChange"
                  />
                </div>

                <!-- Estado -->
                <div
                  v-if="newCoverage.coverage_type === 'state'"
                  class="form-group"
                >
                  <label>Estado (UF):</label>
                  <v-select
                    v-model="newCoverage.uf_id"
                    :options="ufsForCoverage"
                    :reduce="(u) => u.id"
                    label="name"
                    class="vselect"
                    placeholder="Selecione o estado"
                  />
                </div>

                <!-- Município -->
                <template v-if="newCoverage.coverage_type === 'city'">
                  <div class="form-group">
                    <label>Filtrar por UF:</label>
                    <v-select
                      v-model="coverageUfFilter"
                      :options="ufsForCoverageFiltered"
                      :reduce="(u) => u.id"
                      label="name"
                      class="vselect"
                      placeholder="Selecione a UF"
                      :disabled="
                        parentConstraint &&
                          parentConstraint.lockedUfIds.length === 1
                      "
                      @input="loadCitiesForCoverage"
                    />
                  </div>
                  <div class="form-group">
                    <label>Município:</label>
                    <v-select
                      v-model="newCoverage.city_id"
                      :options="citiesForCoverage"
                      :reduce="(c) => c.id"
                      label="name"
                      class="vselect"
                      placeholder="Selecione o município"
                      :disabled="!coverageUfFilter"
                    />
                  </div>
                </template>

                <!-- Bairro -->
                <template v-if="newCoverage.coverage_type === 'neighborhood'">
                  <div
                    v-if="parentConstraint && parentConstraint.neighborhoodHint"
                    class="alert alert-warning py-1 px-2 small mb-1"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="12"
                      class="mr-50"
                    />
                    {{ parentConstraint.neighborhoodHint }}
                  </div>
                  <div class="form-group">
                    <label>Buscar bairro:</label>
                    <gmap-autocomplete
                      class="form-control"
                      placeholder="Ex: Centro, Jardim América..."
                      :options="neighborhoodAutocompleteOptions"
                      @place_changed="onNeighborhoodPlaceSelected"
                    />
                    <small class="text-muted">
                      Digite o nome do bairro e selecione na lista do Google.
                    </small>
                  </div>
                  <div
                    v-if="newCoverage.neighborhood_name"
                    class="alert alert-success py-1 px-2 small"
                  >
                    <strong>Selecionado:</strong>
                    {{ newCoverage.neighborhood_name }}
                  </div>
                </template>

                <b-button
                  v-if="$hasAbility('node.admin')"
                  variant="primary"
                  :disabled="!canAddCoverage"
                  @click="addCoverage"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="14"
                    class="mr-50"
                  />
                  Adicionar
                </b-button>
              </div>
            </div>

            <!-- Lista de coberturas adicionadas -->
            <div
              v-if="coverages.length"
              class="card mt-1"
            >
              <div class="card-header">
                <span class="card-title mb-0">Coberturas definidas</span>
              </div>
              <ul class="list-group list-group-flush">
                <li
                  v-for="cov in coverages"
                  :key="cov.id"
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>
                    <b-badge
                      :variant="coverageBadge(cov.coverage_type)"
                      class="mr-50"
                    >
                      {{ coverageLabel(cov.coverage_type) }}
                    </b-badge>
                    {{ coverageDescription(cov) }}
                  </span>
                  <b-button
                    v-if="$hasAbility('node.admin')"
                    size="sm"
                    variant="outline-danger"
                    @click="removeCoverage(cov)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="12"
                    />
                  </b-button>
                </li>
              </ul>
            </div>
          </div>

          <!-- Mapa -->
          <div class="col-md-8">
            <div class="card">
              <div
                class="card-body p-0"
                style="height: 500px; position: relative"
              >
                <div
                  v-if="coveragesLoading"
                  class="d-flex align-items-center justify-content-center"
                  style="height: 100%"
                >
                  <b-spinner variant="primary" />
                </div>
                <gmap-map
                  v-else
                  ref="coverageMap"
                  :center="mapCenter"
                  :zoom="mapZoom"
                  style="width: 100%; height: 100%"
                  :options="{ streetViewControl: false, mapTypeControl: false }"
                >
                  <gmap-polygon
                    v-for="(poly, idx) in mapPolygons"
                    :key="idx"
                    :paths="poly.paths"
                    :options="{
                      fillColor: poly.color,
                      fillOpacity: 0.25,
                      strokeColor: poly.color,
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                    }"
                  />
                </gmap-map>
              </div>
            </div>
          </div>
        </div>

        <!-- Botão ver árvore de sub-nós -->
        <div
          v-if="$hasAbility('node.admin')"
          class="text-right mt-1"
        >
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="openTreeMap"
          >
            <feather-icon
              icon="MapIcon"
              size="14"
              class="mr-50"
            />
            Ver mapa completo (incluindo sub-nós)
          </b-button>
        </div>
      </b-tab>

      <!-- ── Aba: Integração de Materiais (DIM) ── -->
      <b-tab title="Materiais (DIM)">
        <div class="row">
          <div class="col-md-7">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">Integração de Materiais (DIM)</span>
              </div>
              <div class="card-body">
                <div
                  v-if="dimLoading"
                  class="text-center py-2"
                >
                  <b-spinner small /> Carregando...
                </div>
                <template v-else>
                  <div class="form-group">
                    <label>URL base do DIM</label>
                    <input
                      v-model="dim.base_url"
                      class="form-control"
                      type="text"
                      placeholder="https://dim.exemplo.com/api/v1"
                    >
                  </div>
                  <div class="form-group">
                    <label>Usuário (e-mail)</label>
                    <input
                      v-model="dim.username"
                      class="form-control"
                      type="text"
                      placeholder="usuario@exemplo.com"
                    >
                  </div>
                  <div class="form-group">
                    <label>{{ dim.has_password ? 'Senha (em branco mantém a atual)' : 'Senha' }}</label>
                    <input
                      v-model="dim.password"
                      class="form-control"
                      type="password"
                      autocomplete="new-password"
                      placeholder="••••••••"
                    >
                  </div>
                  <div class="custom-control custom-switch mb-1">
                    <input
                      id="dim-enabled"
                      v-model="dim.enabled"
                      type="checkbox"
                      class="custom-control-input"
                    >
                    <label
                      class="custom-control-label"
                      for="dim-enabled"
                    >Integração ativa</label>
                  </div>
                  <div
                    class="d-flex flex-wrap"
                    style="gap: 8px"
                  >
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      variant="outline-secondary"
                      size="sm"
                      :disabled="dimTesting"
                      @click="testDimConnection"
                    >
                      <b-spinner
                        v-if="dimTesting"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="ZapIcon"
                        size="14"
                        class="mr-50"
                      />
                      Testar conexão
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.admin')"
                      variant="primary"
                      size="sm"
                      :disabled="dimSaving"
                      @click="saveDimConfig"
                    >
                      <b-spinner
                        v-if="dimSaving"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="SaveIcon"
                        size="14"
                        class="mr-50"
                      />
                      Salvar
                    </b-button>
                    <b-button
                      v-if="$hasAbility('node.ver')"
                      variant="outline-primary"
                      size="sm"
                      :to="{ name: 'NodeMaterials', params: { nodeId: node.id } }"
                    >
                      <feather-icon
                        icon="PackageIcon"
                        size="14"
                        class="mr-50"
                      />
                      Gerenciar materiais
                    </b-button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
      <b-tab title="Equipamentos">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <span class="card-title mb-0">Equipamentos deste nó</span>
            <router-link
              :to="{ name: 'list-equipment-types', query: { node_id: node.id } }"
              class="btn btn-sm btn-outline-secondary"
              title="Os tipos são compartilhados por toda a organização"
            >
              Tipos de equipamento
            </router-link>
          </div>
          <div class="card-body">
            <equipment-list
              v-if="node && node.id"
              :node-id="node.id"
            />
          </div>
        </div>
      </b-tab>

    </b-tabs>

    <!-- Modal: mapa de toda a árvore de cobertura -->
    <b-modal
      id="modal-tree-map"
      title="Mapa de Abrangência — Nó e Sub-nós"
      size="xl"
      hide-footer
      @hide="treeMapPolygons = []"
    >
      <div style="height: 600px; position: relative">
        <div
          v-if="treeMapLoading"
          class="d-flex align-items-center justify-content-center"
          style="height: 100%"
        >
          <b-spinner variant="primary" />
          <span class="ml-2">Carregando polígonos...</span>
        </div>
        <gmap-map
          v-else
          :center="treeMapCenter"
          :zoom="treeMapZoom"
          style="width: 100%; height: 100%"
          :options="{ streetViewControl: false, mapTypeControl: false }"
        >
          <gmap-polygon
            v-for="(poly, idx) in treeMapPolygons"
            :key="idx"
            :paths="poly.paths"
            :options="{
              fillColor: poly.color,
              fillOpacity: 0.2,
              strokeColor: poly.color,
              strokeOpacity: 0.9,
              strokeWeight: 2,
            }"
          />
        </gmap-map>
      </div>
      <!-- Legenda por nó -->
      <div
        v-if="treeMapLegend.length"
        class="mt-1 d-flex flex-wrap gap-1"
      >
        <b-badge
          v-for="item in treeMapLegend"
          :key="item.nodeId"
          :style="`background:${item.color};`"
          class="mr-50 mb-50"
        >
          {{ item.nodeName }}
        </b-badge>
      </div>
    </b-modal>

    <div
      v-if="!node.id && loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
    </div>
  </div>
</template>

<script>
import {
  BTabs,
  BTab,
  BButton,
  BModal,
  BSpinner,
  BBadge,
  BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import swal from 'sweetalert'
import EquipmentList from '@/views/Equipment/Index.vue'

export default {
  components: {
    EquipmentList,
    BTabs,
    BTab,
    BButton,
    BModal,
    BSpinner,
    BBadge,
    BFormCheckbox,
    vSelect,
  },

  data() {
    return {
      loading: true,
      node: {},
      fieldSchema: [],

      nodeTypes: [],
      allNodes: [],

      // controle de lazy load por aba (0=Detalhes, 1=Empresa, 2=Sub-nós, 3=Profissionais, 4=Disciplinas, 5=Abrangência, 6=Materiais DIM)
      tabsLoaded: {
        2: false, 3: false, 4: false, 5: false, 6: false,
      },

      // Integração de materiais (DIM)
      dim: {
        base_url: '', username: '', password: '', enabled: false, has_password: false,
      },
      dimLoading: false,
      dimSaving: false,
      dimTesting: false,

      // sub-nós
      newChildId: null,

      // profissionais
      nodeProfessionals: [],
      allProfessionals: [],
      allRoles: [],
      allProfessionalTypes: [],
      newProfessional: {
        professional_id: null,
        role_id: null,
        professional_type_id: null,
      },

      // empresa
      company: {
        name: '',
        fantasy_name: '',
        cnpj: '',
        type: '',
        phone: '',
        cep: '',
        address: '',
        neighborhood: '',
        number: '',
        complement: '',
        reference: '',
        state_id: null,
        city_id: null,
        status: 1,
      },
      ufs: [],
      cities: [],

      // abrangência
      coverages: [],
      coveragesLoading: false,
      ufsForCoverage: [],
      coverageUfFilter: null,
      citiesForCoverage: [],
      newCoverage: {
        coverage_type: '',
        uf_id: null,
        city_id: null,
        neighborhood_name: '',
      },
      parentCoverages: [],
      mapCenter: { lat: -15.77972, lng: -47.92972 },
      mapZoom: 4,
      mapPolygons: [],
      newCoverageViewport: null, // viewport do Google para bairro selecionado

      // modal mapa árvore
      treeMapLoading: false,
      treeMapPolygons: [],
      treeMapLegend: [],
      treeMapCenter: { lat: -15.77972, lng: -47.92972 },
      treeMapZoom: 4,

      // disciplinas
      allDisciplines: [],
      selectedDisciplineIds: [],
      disciplineConfig: {},
    }
  },

  computed: {
    // Deriva sub-nós do allNodes (evita request extra ao servidor)
    children() {
      if (!this.node.id) return []
      return this.allNodes.filter(n => n.parent_id == this.node.id)
    },

    availableChildren() {
      const linkedIds = this.children.map(c => c.id)
      return this.allNodes.filter(
        n => n.id !== this.node.id && !linkedIds.includes(n.id),
      )
    },
    availableProfessionals() {
      const linkedIds = this.nodeProfessionals.map(np => np.professional_id)
      return this.allProfessionals.filter(p => !linkedIds.includes(p.id))
    },

    canAddCoverage() {
      const t = this.newCoverage.coverage_type
      if (!t) return false
      if (t === 'state') return !!this.newCoverage.uf_id
      if (t === 'city') return !!this.newCoverage.city_id
      if (t === 'neighborhood') return !!this.newCoverage.neighborhood_name
      return false
    },

    // Restrição herdada das coberturas do nó pai
    parentConstraint() {
      if (!this.parentCoverages.length) return null

      const types = this.parentCoverages.map(c => c.coverage_type)

      const stateUfIds = [
        ...new Set(
          this.parentCoverages
            .filter(c => c.coverage_type === 'state' && c.uf_id)
            .map(c => c.uf_id),
        ),
      ]
      const cityUfIds = [
        ...new Set(
          this.parentCoverages
            .filter(c => c.coverage_type === 'city' && c.uf_id)
            .map(c => c.uf_id),
        ),
      ]

      if (types.includes('city')) {
        const cityNames = this.parentCoverages
          .filter(c => c.coverage_type === 'city' && c.city)
          .map(c => {
            const uf = c.uf ? c.uf.code : null
            return uf ? `${c.city.name}/${uf}` : c.city.name
          })
        return {
          allowedTypes: ['neighborhood'],
          lockedUfIds: cityUfIds,
          neighborhoodHint: cityNames.length
            ? `Selecione bairros dentro de: ${cityNames.join(', ')}`
            : 'Selecione bairros dentro das cidades do nó pai.',
          message: 'Nó pai cobre municípios específicos. Você só pode definir abrangência por bairro.',
        }
      }

      if (types.includes('state')) {
        const ufNames = this.parentCoverages
          .filter(c => c.coverage_type === 'state' && c.uf)
          .map(c => c.uf.code || c.uf.name)
        return {
          allowedTypes: ['city', 'neighborhood'],
          lockedUfIds: stateUfIds,
          neighborhoodHint: ufNames.length
            ? `Selecione bairros dentro do(s) estado(s): ${ufNames.join(', ')}`
            : null,
          message: ufNames.length
            ? `Nó pai cobre o(s) estado(s): ${ufNames.join(
              ', ',
            )}. Selecione municípios ou bairros dentro desse(s) estado(s).`
            : 'Nó pai possui cobertura por estado. Selecione municípios ou bairros dentro dos estados cobertos.',
        }
      }

      return null
    },

    // Opções de tipo de cobertura filtradas pela restrição do pai
    coverageTypeOptions() {
      const all = [
        { label: 'Estado', value: 'state' },
        { label: 'Município', value: 'city' },
        { label: 'Bairro', value: 'neighborhood' },
      ]
      if (!this.parentConstraint) return all
      return all.filter(o => this.parentConstraint.allowedTypes.includes(o.value))
    },

    // UFs disponíveis para o formulário de cobertura (filtradas pelo pai se necessário)
    ufsForCoverageFiltered() {
      if (!this.parentConstraint || !this.parentConstraint.lockedUfIds.length) {
        return this.ufsForCoverage
      }
      return this.ufsForCoverage.filter(u => this.parentConstraint.lockedUfIds.includes(u.id))
    },

    // Bounding box derivado da cobertura do pai (para bias do autocomplete de bairro)
    parentCoverageBounds() {
      if (!this.parentCoverages.length) return null
      const cov = this.parentCoverages.find(
        c => c.coverage_type === 'city' && c.geojson,
      )
        || this.parentCoverages.find(
          c => c.coverage_type === 'state' && c.geojson,
        )
      if (!cov) return null
      try {
        const geo = typeof cov.geojson === 'string'
          ? JSON.parse(cov.geojson)
          : cov.geojson
        let coords = []
        if (geo.type === 'Polygon') coords = geo.coordinates[0]
        else if (geo.type === 'MultiPolygon') coords = geo.coordinates[0][0]
        if (!coords.length) return null
        const lngs = coords.map(c => c[0])
        const lats = coords.map(c => c[1])
        return {
          north: Math.max(...lats),
          south: Math.min(...lats),
          east: Math.max(...lngs),
          west: Math.min(...lngs),
        }
      } catch {
        return null
      }
    },

    neighborhoodAutocompleteOptions() {
      const opts = {
        types: ['neighborhood', 'sublocality', 'sublocality_level_1'],
        componentRestrictions: { country: 'br' },
        fields: ['name', 'geometry', 'address_components', 'formatted_address'],
      }
      if (this.parentCoverageBounds) {
        opts.bounds = this.parentCoverageBounds
        opts.strictBounds = false
      }
      return opts
    },
  },

  mounted() {
    this.loadAll()
  },

  methods: {
    // ── carregamentos ─────────────────────────────────────────────────────

    loadAll() {
      const { nodeId: id } = this.$route.params
      this.loading = true
      // Reseta lazy load ao navegar entre nós
      this.tabsLoaded = {
        1: false, 3: false, 4: false, 5: false,
      }
      // Carrega dados da aba inicial + contadores de todas as abas
      Promise.all([
        this.loadNode(id),
        this.loadNodeTypes(),
        this.loadAllNodes(), // já inclui with[]=type, sub-nós são derivados daqui
        this.loadNodeProfessionals(id),
        this.loadCoverages(id),
        this.loadNodeDisciplines(id),
      ]).finally(() => {
        this.loading = false
        this.$loading(false)
      })
    },

    loadNode(id) {
      return axios
        .get(
          `${this.$store.state.api}nodes/${id}?with[]=type&with[]=parent`,
        )
        .then(r => {
          const data = r.data.data[0]
          data.metadata = data.metadata || {}
          this.node = data
          // Carrega schema do tipo assim que o nó e os tipos estiverem disponíveis
          this._applyFieldSchema()
          // Carrega coberturas do pai para encadeamento de abrangência
          if (data.parent_id) {
            this.loadParentCoverages(data.parent_id)
          } else {
            this.parentCoverages = []
          }
        })
    },

    loadNodeTypes() {
      return axios
        .get(`${this.$store.state.api}node_types?paginated=false`)
        .then(r => {
          this.nodeTypes = r.data.data
          this._applyFieldSchema()
        })
    },

    _applyFieldSchema() {
      if (!this.node.type_id || !this.nodeTypes.length) return
      const type = this.nodeTypes.find(t => t.id === this.node.type_id)
      this.fieldSchema = type && type.field_schema ? type.field_schema : []
      const meta = this.node.metadata || {}
      this.fieldSchema.forEach(f => {
        if (!(f.key in meta)) meta[f.key] = ''
      })
      this.node.metadata = meta
    },

    loadAllNodes() {
      // Inclui type para que sub-nós derivados já tenham o relacionamento
      return axios
        .get(`${this.$store.state.api}nodes?paginated=false&with[]=type`)
        .then(r => {
          this.allNodes = r.data.data
        })
    },

    loadNodeProfessionals(id) {
      return axios
        .get(
          `${this.$store.state.api
          }node_professionals?paginated=false&node_id=${
            id
          }&with[]=professional&with[]=role&with[]=professionalType`,
        )
        .then(r => {
          this.nodeProfessionals = r.data.data
        })
    },

    loadAllProfessionalTypes() {
      return axios
        .get(
          `${this.$store.state.api}professional_types?paginated=false&status=1`,
        )
        .then(r => {
          this.allProfessionalTypes = r.data.data
        })
    },

    loadAllRoles() {
      return axios
        .get(`${this.$store.state.api}roles?paginated=false&status=1`)
        .then(r => {
          this.allRoles = r.data.data
        })
    },

    loadAllProfessionals() {
      return axios
        .get(`${this.$store.state.api}professionals?paginated=false&status=1`)
        .then(r => {
          this.allProfessionals = r.data.data
        })
    },

    // ── empresa ───────────────────────────────────────────────────────────

    loadNodeCompany(id) {
      return axios
        .get(`${this.$store.state.api}companies?paginated=false&node_id=${id}`)
        .then(r => {
          const list = r.data.data || []
          if (list.length > 0) {
            this.company = list[0]
            if (this.company.state_id) this.getCities()
          } else {
            this.company = {
              name: '',
              fantasy_name: '',
              cnpj: '',
              type: '',
              phone: '',
              cep: '',
              address: '',
              neighborhood: '',
              number: '',
              complement: '',
              reference: '',
              state_id: null,
              city_id: null,
              status: 1,
            }
          }
        })
    },

    loadUfs() {
      return axios
        .get(`${this.$store.state.api}ufs?paginated=false`)
        .then(r => {
          this.ufs = r.data.data
        })
    },

    getCities() {
      if (!this.company.state_id) {
        this.cities = []
        return Promise.resolve()
      }
      return axios
        .get(
          `${this.$store.state.api
          }cities?paginated=false&uf_id=${
            this.company.state_id}`,
        )
        .then(r => {
          this.cities = r.data.data
        })
    },

    buscarCep() {
      const self = this
      if (!/^[0-9]{5}-[0-9]{3}$/.test(this.company.cep)) return
      $.getJSON(
        `https://viacep.com.br/ws/${this.company.cep}/json/`,
        endereco => {
          if (endereco.erro) return
          self.company.address = endereco.logradouro
          self.company.neighborhood = endereco.bairro
          const api = `${self.$store.state.api}cities?code=${endereco.ibge.slice(0, -1)}`
          axios.get(api).then(r => {
            self.company.state_id = r.data.data[0].uf_id
            self.getCities().then(() => {
              self.company.city_id = r.data.data[0].id
            })
          })
          $('#inputNumeroEmpresa').focus()
        },
      )
    },

    saveCompany() {
      const self = this
      const api = `${self.$store.state.api}companies`
      const payload = { ...self.company, node_id: self.node.id }

      self.$loading(true)

      if (self.company.id) {
        payload._method = 'PUT'
        axios
          .post(`${api}/${self.company.id}`, payload)
          .then(() => {
            self.$message(
              'Sucesso',
              'Empresa atualizada com sucesso',
              'success',
            )
            self.$loading(false)
          })
          .catch(e => {
            self.$loading(false)
            self.$message(null, e.response.data, 'error')
          })
      } else {
        axios
          .post(api, payload)
          .then(r => {
            self.company = r.data.data || { ...self.company, ...r.data }
            self.$message(
              'Sucesso',
              'Empresa cadastrada com sucesso',
              'success',
            )
            self.$loading(false)
            self.loadNodeCompany(self.node.id)
          })
          .catch(e => {
            self.$loading(false)
            self.$message(null, e.response.data, 'error')
          })
      }
    },

    unlinkCompany() {
      if (!confirm('Desvincular a empresa deste nó?')) return
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}companies/${this.company.id}`, {
          ...this.company,
          node_id: null,
          _method: 'PUT',
        })
        .then(() => {
          this.$message('Sucesso', 'Empresa desvinculada', 'success')
          this.$loading(false)
          this.loadNodeCompany(this.node.id)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── detalhes do nó ────────────────────────────────────────────────────

    saveNode() {
      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}nodes/${this.node.id}`, {
          ...this.node,
          _method: 'PUT',
        })
        .then(() => {
          this.$message('Sucesso', 'Nó atualizado com sucesso', 'success')
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── sub-nós ───────────────────────────────────────────────────────────

    goToAdmin(id) {
      this.$loading(true)
      this.$router.push(`/nodes/admin/${id}`)
    },

    openAddChild() {
      this.newChildId = null
      this.$bvModal.show('modal-add-child')
    },

    saveChild() {
      if (!this.newChildId) return
      const child = this.allNodes.find(n => n.id === this.newChildId)
      if (!child) return

      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}nodes/${this.newChildId}`, {
          ...child,
          parent_id: this.node.id,
          _method: 'PUT',
        })
        .then(() => {
          this.$message('Sucesso', 'Sub-nó vinculado com sucesso', 'success')
          this.$loading(false)
          this.loadAllNodes()
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    unlinkChild(child) {
      if (!confirm(`Remover "${child.name}" como filho deste nó?`)) return
      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}nodes/${child.id}`, {
          ...child,
          parent_id: null,
          _method: 'PUT',
        })
        .then(() => {
          this.$message('Sucesso', 'Sub-nó desvinculado', 'success')
          this.$loading(false)
          this.loadAllNodes()
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── profissionais ─────────────────────────────────────────────────────

    openAddProfessional() {
      this.newProfessional = {
        professional_id: null,
        role_id: null,
        professional_type_id: null,
      }
      this.$bvModal.show('modal-add-professional')
    },

    saveProfessional() {
      if (!this.newProfessional.professional_id) return
      this.$loading(true)
      this.$http
        .post(`${this.$store.state.api}node_professionals`, {
          node_id: this.node.id,
          professional_id: this.newProfessional.professional_id,
          role_id: this.newProfessional.role_id,
          professional_type_id: this.newProfessional.professional_type_id,
        })
        .then(() => {
          this.$message(
            'Sucesso',
            'Profissional vinculado com sucesso',
            'success',
          )
          this.$loading(false)
          this.loadNodeProfessionals(this.node.id)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── lazy load por aba ──────────────────────────────────────────────────

    onTabChange(tabIndex) {
      const { nodeId: id } = this.$route.params
      if (tabIndex === 1 && !this.tabsLoaded[1]) {
        this.tabsLoaded[1] = true
        Promise.all([this.loadNodeCompany(id), this.loadUfs()])
      } else if (tabIndex === 3 && !this.tabsLoaded[3]) {
        this.tabsLoaded[3] = true
        this.loadAllProfessionals()
        this.loadAllRoles()
        this.loadAllProfessionalTypes()
      } else if (tabIndex === 4 && !this.tabsLoaded[4]) {
        this.tabsLoaded[4] = true
        this.loadAllDisciplines()
      } else if (tabIndex === 5 && !this.tabsLoaded[5]) {
        this.tabsLoaded[5] = true
        this.loadUfsForCoverage()
      } else if (tabIndex === 6 && !this.tabsLoaded[6]) {
        this.tabsLoaded[6] = true
        this.loadDimConfig()
      }
    },

    // ── Integração de materiais (DIM) ──────────────────────────────────────
    loadDimConfig() {
      this.dimLoading = true
      this.$http
        .get(`${this.$store.state.api}materials/config`, { params: { node_id: this.node.id } })
        .then(r => {
          const c = r.data.data
          this.dim = c
            ? {
              base_url: c.base_url || '',
              username: c.username || '',
              password: '',
              enabled: !!c.enabled,
              has_password: !!c.has_password,
            }
            : {
              base_url: '', username: '', password: '', enabled: false, has_password: false,
            }
        })
        .catch(() => {})
        .finally(() => { this.dimLoading = false })
    },

    saveDimConfig() {
      this.dimSaving = true
      const payload = {
        node_id: this.node.id,
        base_url: this.dim.base_url,
        username: this.dim.username,
        enabled: this.dim.enabled,
      }
      if (this.dim.password) payload.password = this.dim.password
      this.$http
        .post(`${this.$store.state.api}materials/config`, payload)
        .then(r => {
          this.$message('Sucesso', 'Configuração DIM salva.', 'success')
          this.dim.has_password = !!(r.data.data && r.data.data.has_password)
          this.dim.password = ''
        })
        .catch(e => this.$message(null, (e.response && e.response.data) || 'Erro ao salvar', 'error'))
        .finally(() => { this.dimSaving = false })
    },

    testDimConnection() {
      this.dimTesting = true
      const payload = { node_id: this.node.id, base_url: this.dim.base_url, username: this.dim.username }
      if (this.dim.password) payload.password = this.dim.password
      this.$http
        .post(`${this.$store.state.api}materials/config/test`, payload)
        .then(r => {
          if (r.data && r.data.ok) this.$message('Conexão OK', 'Autenticado no DIM com sucesso.', 'success')
          else this.$message('Falha', (r.data && r.data.message) || 'Não foi possível conectar.', 'error')
        })
        .catch(() => this.$message('Falha', 'Não foi possível conectar ao DIM.', 'error'))
        .finally(() => { this.dimTesting = false })
    },

    unlinkProfessional(np) {
      const name = np.professional ? np.professional.name : 'profissional'
      if (!confirm(`Desvincular "${name}" deste nó?`)) return
      this.$loading(true)
      this.$http
        .delete(`${this.$store.state.api}node_professionals/${np.id}`)
        .then(() => {
          this.$message('Sucesso', 'Profissional desvinculado', 'success')
          this.$loading(false)
          this.loadNodeProfessionals(this.node.id)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // ── abrangência ────────────────────────────────────────────────────────

    loadCoverages(id) {
      this.coveragesLoading = true
      return axios
        .get(
          `${this.$store.state.api
          }node_coverages?paginated=false&node_id=${
            id
          }&with[]=uf&with[]=city`,
        )
        .then(r => {
          this.coverages = r.data.data
          this.rebuildMapPolygons()
        })
        .finally(() => {
          this.coveragesLoading = false
        })
    },

    loadUfsForCoverage() {
      if (this.ufsForCoverage.length) return Promise.resolve()
      return axios
        .get(`${this.$store.state.api}ufs?paginated=false`)
        .then(r => {
          this.ufsForCoverage = r.data.data
        })
    },

    loadCitiesForCoverage() {
      // Sync uf_id so it gets persisted for city-type coverages
      this.newCoverage.uf_id = this.coverageUfFilter || null
      if (!this.coverageUfFilter) {
        this.citiesForCoverage = []
        return
      }
      axios
        .get(
          `${this.$store.state.api
          }cities?paginated=false&uf_id=${
            this.coverageUfFilter}`,
        )
        .then(r => {
          this.citiesForCoverage = r.data.data
          this.newCoverage.city_id = null
        })
    },

    onCoverageTypeChange() {
      this.newCoverage.uf_id = null
      this.newCoverage.city_id = null
      this.newCoverage.neighborhood_name = ''
      this.coverageUfFilter = null
      this.citiesForCoverage = []
      this.newCoverageViewport = null

      // Auto-aplicar UF travada quando o pai restringe a um único estado
      if (
        this.newCoverage.coverage_type === 'city'
        && this.parentConstraint
        && this.parentConstraint.lockedUfIds.length === 1
      ) {
        this.coverageUfFilter = this.parentConstraint.lockedUfIds[0]
        this.loadCitiesForCoverage()
      }
    },

    loadParentCoverages(parentId) {
      return axios
        .get(
          `${this.$store.state.api
          }node_coverages?paginated=false&node_id=${
            parentId
          }&with[]=uf&with[]=city`,
        )
        .then(r => {
          this.parentCoverages = r.data.data
        })
    },

    onNeighborhoodPlaceSelected(place) {
      if (!place || !place.geometry) return

      // ── Valida se o bairro está dentro da cobertura do nó pai ──────────
      if (this.parentConstraint && place.address_components) {
        const getComponent = types => {
          const comp = place.address_components.find(c => types.every(t => c.types.includes(t)))
          return comp ? comp.long_name : null
        }
        const getComponentShort = types => {
          const comp = place.address_components.find(c => types.every(t => c.types.includes(t)))
          return comp ? comp.short_name : null
        }

        // Estado do lugar selecionado (nome longo e abreviação)
        const placeStateLong = getComponent(['administrative_area_level_1'])
        const placeStateShort = getComponentShort([
          'administrative_area_level_1',
        ])
        // Município do lugar selecionado
        const placeCityLong = getComponent(['administrative_area_level_2'])

        // Pai com cobertura de município → valida estado E município
        const parentCities = this.parentCoverages.filter(
          c => c.coverage_type === 'city' && c.city,
        )
        if (parentCities.length) {
          const matchCity = parentCities.some(c => {
            const nameOk = c.city.name
              && placeCityLong
              && c.city.name
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                === placeCityLong
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
            return nameOk
          })
          if (!matchCity) {
            const cityNames = parentCities.map(c => c.city.name).join(', ')
            this.$message(
              'Bairro inválido',
              `Selecione um bairro dentro de: ${cityNames}.`,
              'error',
            )
            this.newCoverage.neighborhood_name = ''
            this.newCoverageViewport = null
            return
          }
        } else {
          // Pai com cobertura de estado → valida apenas estado
          const parentUfs = this.parentCoverages.filter(
            c => c.coverage_type === 'state' && c.uf,
          )
          if (parentUfs.length) {
            const matchState = parentUfs.some(c => (
              (c.uf.code && c.uf.code === placeStateShort)
                || (c.uf.name
                  && placeStateLong
                  && c.uf.name
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                    === placeStateLong
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase())
            ))
            if (!matchState) {
              const ufNames = parentUfs
                .map(c => c.uf.code || c.uf.name)
                .join(', ')
              this.$message(
                'Bairro inválido',
                `Selecione um bairro dentro do(s) estado(s): ${ufNames}.`,
                'error',
              )
              this.newCoverage.neighborhood_name = ''
              this.newCoverageViewport = null
              return
            }
          }
        }
      }

      this.newCoverage.neighborhood_name = place.name || place.formatted_address || ''
      // Salva o viewport do Google (bounding box) para exibir no mapa
      if (place.geometry.viewport) {
        const vp = place.geometry.viewport
        const ne = {
          lat: vp.getNorthEast().lat(),
          lng: vp.getNorthEast().lng(),
        }
        const sw = {
          lat: vp.getSouthWest().lat(),
          lng: vp.getSouthWest().lng(),
        }
        // Converte viewport em polígono retangular (GeoJSON)
        this.newCoverageViewport = {
          type: 'Polygon',
          coordinates: [
            [
              [sw.lng, sw.lat],
              [ne.lng, sw.lat],
              [ne.lng, ne.lat],
              [sw.lng, ne.lat],
              [sw.lng, sw.lat],
            ],
          ],
        }
        // Centraliza o mapa no lugar selecionado
        const loc = place.geometry.location
        this.mapCenter = { lat: loc.lat(), lng: loc.lng() }
        this.mapZoom = 14
      }
    },

    async addCoverage() {
      this.$loading(true)

      // Para estado e município, busca o polígono agora e salva no banco
      // (bairro já tem o viewport do Google em newCoverageViewport)
      let geojson = this.newCoverageViewport
        ? JSON.stringify(this.newCoverageViewport)
        : null

      if (!geojson) {
        let params = null
        if (
          this.newCoverage.coverage_type === 'state'
          && this.newCoverage.uf_id
        ) {
          const uf = this.ufsForCoverage.find(
            u => u.id === this.newCoverage.uf_id,
          )
          if (uf) params = { state: uf.name }
        } else if (
          this.newCoverage.coverage_type === 'city'
          && this.newCoverage.city_id
        ) {
          const city = this.citiesForCoverage.find(
            c => c.id === this.newCoverage.city_id,
          )
          const uf = this.ufsForCoverage.find(
            u => u.id === this.newCoverage.uf_id,
          )
          if (city) params = { city: city.name, ...(uf ? { state: uf.name } : {}) }
        }
        if (params) {
          const paths = await this.fetchPolygon(params)
          if (paths) {
            // Converte paths de volta para GeoJSON
            const toCoords = path => [
              ...path.map(({ lat, lng }) => [lng, lat]),
              [path[0].lng, path[0].lat],
            ]
            if (paths.length === 1) {
              geojson = JSON.stringify({
                type: 'Polygon',
                coordinates: [toCoords(paths[0])],
              })
            } else {
              geojson = JSON.stringify({
                type: 'MultiPolygon',
                coordinates: paths.map(p => [toCoords(p)]),
              })
            }
          }
        }
      }

      const payload = {
        node_id: this.node.id,
        coverage_type: this.newCoverage.coverage_type,
        uf_id: this.newCoverage.uf_id || null,
        city_id: this.newCoverage.city_id || null,
        neighborhood_name: this.newCoverage.neighborhood_name || null,
        geojson,
      }

      axios
        .post(`${this.$store.state.api}node_coverages`, payload)
        .then(() => {
          this.$message('Sucesso', 'Abrangência adicionada', 'success')
          this.$loading(false)
          this.newCoverage = {
            coverage_type: '',
            uf_id: null,
            city_id: null,
            neighborhood_name: '',
          }
          this.coverageUfFilter = null
          this.citiesForCoverage = []
          this.newCoverageViewport = null
          this.loadCoverages(this.node.id)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    removeCoverage(cov) {
      swal({
        title: 'Remover abrangência?',
        text: `"${this.coverageDescription(
          cov,
        )}" será removida permanentemente.`,
        icon: 'warning',
        buttons: {
          cancel: 'Cancelar',
          confirm: { text: 'Remover', className: 'btn-danger' },
        },
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}node_coverages/${cov.id}`)
          .then(() => {
            this.$message('Sucesso', 'Abrangência removida', 'success')
            this.$loading(false)
            this.loadCoverages(this.node.id)
          })
          .catch(e => {
            this.$loading(false)
            this.$message(null, e.response.data, 'error')
          })
      })
    },

    openTreeMap() {
      this.treeMapPolygons = []
      this.treeMapLegend = []
      this.treeMapLoading = true
      this.$bvModal.show('modal-tree-map')

      axios
        .get(`${this.$store.state.api}node_coverages_tree/${this.node.id}`)
        .then(r => {
          const coverages = r.data
          const toLatLng = coords => coords.map(([lng, lat]) => ({ lat, lng }))

          // Assign a distinct color per node
          const palette = [
            '#1976D2',
            '#388E3C',
            '#F57C00',
            '#7B1FA2',
            '#D32F2F',
            '#00796B',
            '#FBC02D',
            '#5D4037',
            '#0288D1',
            '#C2185B',
          ]
          const nodeColorMap = {}
          let colorIdx = 0

          const polygons = []
          const legendMap = {}

          coverages.forEach(cov => {
            if (!cov.geojson) return
            const nodeId = cov.node_id
            if (!nodeColorMap[nodeId]) {
              nodeColorMap[nodeId] = palette[colorIdx % palette.length]
              colorIdx++
              legendMap[nodeId] = {
                nodeId,
                nodeName: cov.node ? cov.node.name : `Nó #${nodeId}`,
                color: nodeColorMap[nodeId],
              }
            }
            const color = nodeColorMap[nodeId]
            try {
              const geo = typeof cov.geojson === 'string'
                ? JSON.parse(cov.geojson)
                : cov.geojson
              let paths = null
              if (geo.type === 'Polygon') paths = [toLatLng(geo.coordinates[0])]
              else if (geo.type === 'MultiPolygon') paths = geo.coordinates.map(p => toLatLng(p[0]))
              if (paths) paths.forEach(p => polygons.push({ paths: p, color }))
            } catch {}
          })

          this.treeMapPolygons = polygons
          this.treeMapLegend = Object.values(legendMap)

          // Center on first polygon
          if (polygons.length && polygons[0].paths.length) {
            this.treeMapCenter = polygons[0].paths[0]
            this.treeMapZoom = 8
          }
        })
        .finally(() => {
          this.treeMapLoading = false
        })
    },

    coverageLabel(type) {
      return (
        { state: 'Estado', city: 'Município', neighborhood: 'Bairro' }[type]
        || type
      )
    },

    coverageBadge(type) {
      return (
        { state: 'info', city: 'primary', neighborhood: 'success' }[type]
        || 'secondary'
      )
    },

    coverageDescription(cov) {
      // Helper: resolve UF code from cov.uf or fallback via city.uf_id
      const resolveUfCode = () => {
        if (cov.uf) return cov.uf.code
        if (cov.city && cov.city.uf_id) {
          const found = this.ufsForCoverage.find(
            u => u.id === cov.city.uf_id,
          )
          if (found) return found.code
        }
        return null
      }

      if (cov.coverage_type === 'state') return cov.uf ? cov.uf.name : cov.uf_id
      if (cov.coverage_type === 'city') {
        const city = cov.city ? cov.city.name : cov.city_id
        const uf = resolveUfCode()
        return uf ? `${city} / ${uf}` : city
      }
      if (cov.coverage_type === 'neighborhood') {
        const uf = resolveUfCode()
        return uf ? `${cov.neighborhood_name} / ${uf}` : cov.neighborhood_name
      }
      return '-'
    },

    // ── disciplinas ────────────────────────────────────────────────────────

    loadAllDisciplines() {
      return axios
        .get(`${this.$store.state.api}discipline?paginated=false&status=1`)
        .then(r => {
          this.allDisciplines = r.data.data
        })
    },

    loadNodeDisciplines(id) {
      return axios
        .get(`${this.$store.state.api}node_disciplines?node_id=${id}`)
        .then(r => {
          this.selectedDisciplineIds = r.data.data.map(Number)
        })
    },

    toggleDiscipline(id) {
      const idx = this.selectedDisciplineIds.indexOf(id)
      if (idx === -1) {
        this.selectedDisciplineIds.push(id)
      } else {
        this.selectedDisciplineIds.splice(idx, 1)
      }
    },

    openDisciplineConfig(disc) {
      this.$router.push(`/nodes/admin/${this.node.id}/discipline/${disc.id}`)
    },

    saveDisciplines() {
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}node_disciplines/sync`, {
          node_id: this.node.id,
          discipline_ids: this.selectedDisciplineIds,
        })
        .then(() => {
          this.$message(
            'Sucesso',
            'Disciplinas atualizadas com sucesso',
            'success',
          )
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response.data, 'error')
        })
    },

    // Busca polígono no Nominatim (OSM) e retorna array de paths para o GMap
    async fetchPolygon(params) {
      try {
        const base = 'https://nominatim.openstreetmap.org/search'
        const qs = new URLSearchParams({
          ...params,
          countrycodes: 'br',
          format: 'json',
          polygon_geojson: '1',
          limit: '5',
        })
        const res = await fetch(`${base}?${qs}`, {
          headers: { 'Accept-Language': 'pt-BR' },
        })
        const data = await res.json()
        if (!data.length) return null
        // Prefer entries that actually have a geojson polygon/multipolygon
        const withPoly = data.filter(
          d => d.geojson
            && (d.geojson.type === 'Polygon' || d.geojson.type === 'MultiPolygon'),
        )
        if (!withPoly.length) return null
        const geo = withPoly[0].geojson
        const toLatLng = coords => coords.map(([lng, lat]) => ({ lat, lng }))
        if (geo.type === 'Polygon') return [toLatLng(geo.coordinates[0])]
        if (geo.type === 'MultiPolygon') return geo.coordinates.map(poly => toLatLng(poly[0]))
        return null
      } catch {
        return null
      }
    },

    rebuildMapPolygons() {
      const colors = ['#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#D32F2F']
      const polygons = []
      const toLatLng = coords => coords.map(([lng, lat]) => ({ lat, lng }))
      let firstCenter = null
      let firstZoom = 4

      for (let i = 0; i < this.coverages.length; i++) {
        const cov = this.coverages[i]
        const color = colors[i % colors.length]
        if (!cov.geojson) continue
        try {
          const geo = typeof cov.geojson === 'string'
            ? JSON.parse(cov.geojson)
            : cov.geojson
          let paths = null
          if (geo.type === 'Polygon') paths = [toLatLng(geo.coordinates[0])]
          else if (geo.type === 'MultiPolygon') paths = geo.coordinates.map(p => toLatLng(p[0]))
          if (!paths) continue
          paths.forEach(p => polygons.push({ paths: p, color }))
          if (!firstCenter && paths[0].length) {
            firstCenter = paths[0][0]
            firstZoom = cov.coverage_type === 'state'
              ? 6
              : cov.coverage_type === 'city'
                ? 10
                : 14
          }
        } catch {}
      }
      this.mapPolygons = polygons
      if (firstCenter) {
        this.mapCenter = firstCenter
        this.mapZoom = firstZoom
      }
    },
  },

  // Recarrega ao navegar entre nós (admin de filho para pai etc.)
  beforeRouteUpdate(to, from, next) {
    next()
    this.$nextTick(() => this.loadAll())
  },
}
</script>

<style scoped>
.required {
  color: red;
}
</style>
